import { useState, useEffect, useMemo } from "react";
import { Mail, Inbox, Send, FileText, Star, Trash2, RefreshCw, Edit, LogIn, Sparkles, Search, Archive, Flag, ChevronLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";

const folders = [
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "flagged", label: "Flagged", icon: Flag },
  { id: "drafts", label: "Drafts", icon: FileText },
  { id: "sent", label: "Sent", icon: Send },
  { id: "trash", label: "Trash", icon: Trash2 },
  { id: "archive", label: "Archive", icon: Archive },
];

interface GmailMessage {
  id: string;
  threadId: string;
  snippet: string;
  payload?: {
    headers: Array<{ name: string; value: string }>;
    body?: { data?: string };
    parts?: Array<{ body?: { data?: string }; mimeType?: string }>;
  };
  labelIds?: string[];
  internalDate?: string;
}

type GmailPayloadPart = {
  mimeType?: string;
  body?: { data?: string };
  parts?: GmailPayloadPart[];
};

const decodeBase64Url = (value?: string) => {
  if (!value) return "";
  try {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return "";
  }
};

const collectMimeBody = (part: GmailPayloadPart | undefined, mimeType: string): string | null => {
  if (!part) return null;
  if (part.mimeType === mimeType && part.body?.data) {
    const decoded = decodeBase64Url(part.body.data);
    if (decoded.trim()) return decoded;
  }

  for (const child of part.parts ?? []) {
    const match = collectMimeBody(child, mimeType);
    if (match) return match;
  }

  return null;
};

const stripHtmlToText = (html: string) =>
  html
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<li>/gi, "• ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

const normalizePlainText = (text: string) =>
  text
    .replace(/\r\n/g, "\n")
    .replace(/https?:\/\/[^\s]+/g, (match) => match)
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const decodeHtmlEntities = (value: string) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
};

const looksLikeHtml = (value: string) => {
  const decoded = decodeHtmlEntities(value).trim();
  return /<(?:!doctype|html|head|body|table|div|p|span|a|img|style|meta)\b/i.test(decoded);
};

const sanitizeEmailHtml = (html: string) =>
  DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["style", "script", "iframe", "object", "embed"],
  });

function EmailWorkspaceContent() {
  const navigate = useNavigate();
  const [selectedFolder, setSelectedFolder] = useState("inbox");
  const [emails, setEmails] = useState<any[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<any>(null);
  const [isComposing, setIsComposing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isGeneratingReply, setIsGeneratingReply] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [mailboxCounts, setMailboxCounts] = useState({
    total: 0,
    unread: 0,
    flagged: 0,
    inbox: 0,
    drafts: 0,
    sent: 0,
    trash: 0,
    archive: 0,
  });
  const { toast } = useToast();

  const [composeForm, setComposeForm] = useState({
    to: "",
    subject: "",
    body: "",
  });
  const [aiDescription, setAiDescription] = useState("");
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);

  const filteredEmails = useMemo(() => {
    if (!searchQuery.trim()) return emails;
    const query = searchQuery.toLowerCase();
    return emails.filter(email => 
      email.subject?.toLowerCase().includes(query) ||
      email.from_name?.toLowerCase().includes(query) ||
      email.from_email?.toLowerCase().includes(query) ||
      email.body_text?.toLowerCase().includes(query)
    );
  }, [emails, searchQuery]);

  const folderCounts = useMemo(() => ({
    inbox: mailboxCounts.inbox,
    flagged: mailboxCounts.flagged,
    sent: mailboxCounts.sent,
    drafts: mailboxCounts.drafts,
    trash: mailboxCounts.trash,
    archive: mailboxCounts.archive,
  }), [mailboxCounts]);

  // Check for pre-filled recipient from session storage
  useEffect(() => {
    const prefilledRecipient = sessionStorage.getItem('email_compose_to');
    if (prefilledRecipient) {
      setComposeForm(prev => ({ ...prev, to: prefilledRecipient }));
      setIsComposing(true);
      sessionStorage.removeItem('email_compose_to');
      toast({
        title: "Compose email",
        description: `Starting new email to ${prefilledRecipient}`,
      });
    }
  }, []);

  // Check connection status on mount
  useEffect(() => {
    checkGmailConnection();
  }, []);

  const checkGmailConnection = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('gmail-oauth', {
        body: { action: 'status' }
      });

      if (error) {
        const message = typeof error.context === 'object' && error.context && 'error' in error.context
          ? String((error.context as { error?: string }).error)
          : error.message;
        throw new Error(message || "Please try again");
      }

      if (data?.connected && data?.access_token) {
        setAccessToken(data.access_token);
        setIsConnected(true);
        fetchGmailMessages(data.access_token);
      }
    } catch (error: any) {
      console.error('Connection check failed:', error);
    }
  };

  const startGmailAuth = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('gmail-oauth', {
        body: { action: 'authorize' }
      });

      if (error) throw error;

      if (data?.auth_url) {
        // Open OAuth window
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        
        window.open(
          data.auth_url,
          'Gmail Authorization',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Poll for connection
        const checkInterval = setInterval(async () => {
          const { data: statusData } = await supabase.functions.invoke('gmail-oauth', {
            body: { action: 'status' }
          });

          if (statusData?.connected) {
            clearInterval(checkInterval);
            setAccessToken(statusData.access_token);
            setIsConnected(true);
            setLoading(false);
            toast({
              title: "Connected to Gmail",
              description: "Loading your emails...",
            });
            fetchGmailMessages(statusData.access_token);
          }
        }, 2000);

        // Stop polling after 2 minutes
        setTimeout(() => clearInterval(checkInterval), 120000);
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      toast({
        title: "Authentication failed",
        description: error.message || "Could not connect to Google",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const disconnectGmail = async () => {
    try {
      const { error } = await supabase.functions.invoke('gmail-oauth', {
        body: { action: 'disconnect' }
      });

      if (error) throw error;

      setAccessToken(null);
      setIsConnected(false);
      setEmails([]);
      toast({
        title: "Disconnected from Gmail",
        description: "You can reconnect anytime",
      });
    } catch (error: any) {
      console.error('Disconnect error:', error);
      toast({
        title: "Error",
        description: "Failed to disconnect",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (accessToken && isConnected) {
      setSelectedEmail(null);
      fetchGmailMessages(accessToken);
    }
  }, [selectedFolder, accessToken, isConnected]);

  useEffect(() => {
    if (accessToken && isConnected) {
      fetchMailboxCounts(accessToken);
    }
  }, [accessToken, isConnected]);

  const fetchGmailMessages = async (token: string) => {
    setLoading(true);
    try {
      let requestBase = 'https://gmail.googleapis.com/gmail/v1/users/me/messages?maxResults=100';
      let requestFilter = 'labelIds=INBOX';

      switch (selectedFolder) {
        case 'inbox':
          requestFilter = 'labelIds=INBOX';
          break;
        case 'sent':
          requestFilter = 'labelIds=SENT';
          break;
        case 'drafts':
          requestFilter = 'labelIds=DRAFT';
          break;
        case 'flagged':
          requestFilter = 'labelIds=STARRED';
          break;
        case 'trash':
          requestFilter = 'labelIds=TRASH';
          break;
        case 'archive':
          requestFilter = `q=${encodeURIComponent('in:all -in:inbox -in:sent -in:drafts -in:trash -in:spam')}`;
          break;
        default:
          requestFilter = 'labelIds=INBOX';
      }

      let allMessageIds: { id: string }[] = [];
      let pageToken: string | undefined = undefined;

      do {
        const url = pageToken
          ? `${requestBase}&${requestFilter}&pageToken=${pageToken}`
          : `${requestBase}&${requestFilter}`;
        
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          // Handle expired/invalid token
          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('gmail_access_token');
            setAccessToken(null);
            throw new Error('Session expired. Please sign in again.');
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error?.message || 'Failed to fetch messages');
        }

        const data = await response.json();
        
        if (!data.messages || data.messages.length === 0) {
          if (allMessageIds.length === 0) {
            setEmails([]);
            setLoading(false);
            return;
          }
          break;
        }

        allMessageIds = [...allMessageIds, ...data.messages];
        pageToken = data.nextPageToken;
        
        // Limit to first 100 for performance
        if (allMessageIds.length >= 100) break;
        
      } while (pageToken);

      console.log(`Fetched ${allMessageIds.length} emails from ${selectedFolder}`);

      // Fetch full details for messages in batches
      const batchSize = 20;
      const fullMessages: any[] = [];
      
      for (let i = 0; i < Math.min(allMessageIds.length, 100); i += batchSize) {
        const batch = allMessageIds.slice(i, i + batchSize);
        const batchMessages = await Promise.all(
          batch.map(async (msg: { id: string }) => {
            const msgResponse = await fetch(
              `https://gmail.googleapis.com/gmail/v1/users/me/messages/${msg.id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            return msgResponse.json();
          })
        );
        fullMessages.push(...batchMessages);
      }

      const formattedEmails = fullMessages.map((msg: GmailMessage) => {
        const headers = msg.payload?.headers || [];
        const getHeader = (name: string) => 
          headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value || '';

        const subject = getHeader('Subject');
        const from = getHeader('From');
        const to = getHeader('To');
        const date = msg.internalDate ? new Date(parseInt(msg.internalDate)) : new Date();
        
        const htmlBody = collectMimeBody(msg.payload as GmailPayloadPart | undefined, 'text/html') ?? '';
        const plainBody = collectMimeBody(msg.payload as GmailPayloadPart | undefined, 'text/plain')
          ?? decodeBase64Url(msg.payload?.body?.data)
          ?? '';

        const fallbackHtml = !htmlBody.trim() && plainBody.trim() && looksLikeHtml(plainBody)
          ? decodeHtmlEntities(plainBody)
          : '';
        const emailHtml = htmlBody.trim() ? htmlBody : fallbackHtml;
        const cleanedText = emailHtml.trim()
          ? stripHtmlToText(emailHtml)
          : plainBody.trim()
            ? normalizePlainText(plainBody)
            : normalizePlainText(msg.snippet || '');

        const cleanedHtml = emailHtml.trim() ? sanitizeEmailHtml(emailHtml) : '';

        const isUnread = msg.labelIds?.includes('UNREAD') ?? false;
        const isStarred = msg.labelIds?.includes('STARRED') ?? false;

        return {
          id: msg.id,
          subject: subject || '(No Subject)',
          from_email: from,
          from_name: from.split('<')[0].trim(),
          to_email: [to],
          body_text: cleanedText,
          body_html: cleanedHtml,
          created_at: date.toISOString(),
          is_read: !isUnread,
          is_starred: isStarred,
          folder: selectedFolder,
        };
      });

      setEmails(formattedEmails);
    } catch (error: any) {
      console.error('Gmail fetch error:', error);
      
      // Check if it's an auth error
      const isAuthError = error.message?.includes('Session expired') || 
                         error.message?.includes('401') || 
                         error.message?.includes('403') ||
                         error.message?.includes('invalid_grant');
      
      if (isAuthError) {
        setAccessToken(null);
        setIsConnected(false);
        toast({
          title: "Session Expired",
          description: "Reconnecting...",
        });
        // Auto-reconnect by checking connection (will refresh token)
        checkGmailConnection();
      } else {
        toast({
          title: "Error loading emails",
          description: error.message || "Failed to connect to Gmail",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMailboxCounts = async (token: string) => {
    const authHeaders = {
      Authorization: `Bearer ${token}`,
    };

    const countMessages = async (query: string) => {
      let total = 0;
      let pageToken: string | undefined;

      do {
        const params = new URLSearchParams({
          maxResults: '500',
          q: query,
        });

        if (pageToken) {
          params.set('pageToken', pageToken);
        }

        const response = await fetch(`https://gmail.googleapis.com/gmail/v1/users/me/messages?${params.toString()}`, {
          headers: authHeaders,
          cache: 'no-store',
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => '');
          throw new Error(`Failed to load mailbox count for query "${query}": ${response.status} ${errorText}`);
        }

        const data = await response.json();
        total += data.messages?.length ?? 0;
        pageToken = data.nextPageToken;
      } while (pageToken);

      return total;
    };

    const countQueries = {
      inbox: 'in:inbox',
      unread: 'in:inbox is:unread',
      flagged: 'is:starred -in:drafts -in:trash',
      drafts: 'in:drafts',
      sent: 'in:sent',
      trash: 'in:trash',
      archive: 'in:all -in:inbox -in:sent -in:drafts -in:trash -in:spam -in:chats',
      total: 'in:all -in:spam',
    } as const;

    try {
      const entries = Object.entries(countQueries) as Array<[keyof typeof countQueries, string]>;
      const settled = await Promise.allSettled(entries.map(([, query]) => countMessages(query)));

      const nextCounts = entries.reduce((acc, [key], index) => {
        const result = settled[index];
        acc[key] = result.status === 'fulfilled' ? result.value : 0;
        if (result.status === 'rejected') {
          console.error(`Failed to load ${key} mailbox count:`, result.reason);
        }
        return acc;
      }, {} as Record<keyof typeof countQueries, number>);

      setMailboxCounts({
        total: nextCounts.total,
        unread: nextCounts.unread,
        flagged: nextCounts.flagged,
        inbox: nextCounts.inbox,
        drafts: nextCounts.drafts,
        sent: nextCounts.sent,
        trash: nextCounts.trash,
        archive: nextCounts.archive,
      });
    } catch (error) {
      console.error('Failed to load mailbox counts:', error);
    }
  };

  const askBridgette = () => {
    window.dispatchEvent(new CustomEvent('bridgette-open'));
  };

  const handleCompose = async () => {
    if (!isConnected) {
      toast({
        title: "Not authenticated",
        description: "Please sign in with Google first",
        variant: "destructive",
      });
      return;
    }

    try {
      // Send via tracked edge function
      const { data, error } = await supabase.functions.invoke('gmail-send-tracked', {
        body: {
          to: composeForm.to,
          subject: composeForm.subject,
          body: composeForm.body,
          enableTracking: true,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: "Email sent",
        description: `Email sent to ${composeForm.to} with tracking`,
      });

      setIsComposing(false);
      setComposeForm({ to: "", subject: "", body: "" });

      if (accessToken) {
        fetchMailboxCounts(accessToken);
      }
      
      if (selectedFolder === 'sent') {
        fetchGmailMessages(accessToken!);
      }
    } catch (error: any) {
      toast({
        title: "Error sending email",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const markAsRead = async (messageId: string) => {
    if (!accessToken) return;

    try {
      await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}/modify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            removeLabelIds: ['UNREAD'],
          }),
        }
      );
      
      setEmails(prev => prev.map(e => 
        e.id === messageId ? { ...e, is_read: true } : e
      ));

      fetchMailboxCounts(accessToken);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const toggleStar = async (messageId: string, currentStarred: boolean) => {
    if (!accessToken) return;

    try {
      await fetch(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}/modify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            addLabelIds: currentStarred ? [] : ['STARRED'],
            removeLabelIds: currentStarred ? ['STARRED'] : [],
          }),
        }
      );
      
      setEmails(prev => prev.map(e => 
        e.id === messageId ? { ...e, is_starred: !currentStarred } : e
      ));

      fetchMailboxCounts(accessToken);
    } catch (error) {
      console.error('Failed to toggle star:', error);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('gmail_access_token');
    setAccessToken(null);
    setEmails([]);
    setSelectedEmail(null);
    toast({
      title: "Signed out",
      description: "You have been signed out of Gmail",
    });
  };

  const generateAIContent = async () => {
    if (!aiDescription.trim()) {
      toast({
        title: "Description required",
        description: "Please describe what you want the email to say",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingContent(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-email-content', {
        body: {
          description: aiDescription,
          recipient: composeForm.to || null
        }
      });

      if (error) throw error;

      if (data?.content) {
        setComposeForm(prev => ({ ...prev, body: data.content }));
        setAiDescription("");
        toast({
          title: "Email generated",
          description: "AI has written your email content",
        });
      }
    } catch (error: any) {
      console.error('Error generating email:', error);
      toast({
        title: "Failed to generate email",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const generateAIReply = async (email: any) => {
    setIsGeneratingReply(true);
    try {
      const { data, error } = await supabase.functions.invoke('email-ai-reply', {
        body: {
          originalEmail: {
            from: email.from_email,
            subject: email.subject,
            body: email.body_text
          }
        }
      });

      if (error) throw error;

      if (data?.reply) {
        setComposeForm({
          to: email.from_email,
          subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
          body: data.reply
        });
        setIsComposing(true);
        toast({
          title: "AI Reply Generated",
          description: "Review and edit the draft before sending",
        });
      }
    } catch (error: any) {
      console.error('Error generating AI reply:', error);
      toast({
        title: "Failed to generate reply",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingReply(false);
    }
  };

  if (!accessToken) {
    return (
      <div className="min-h-full bg-gradient-to-br from-background via-background to-primary/5">
        <div className="px-4 md:px-6 py-6 max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Sidebar */}
            <div className="lg:col-span-3">
              <Card className="p-5 border-border/60 bg-card/80 backdrop-blur-sm shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-lg font-bold text-foreground leading-tight">InboxGenius</h1>
                    <p className="text-xs text-muted-foreground truncate">Centralized email hub</p>
                  </div>
                </div>

                <div className="space-y-1 mb-6">
                  {folders.map((folder) => (
                    <button
                      key={folder.id}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all hover:bg-muted/60 group"
                    >
                      <div className="flex items-center gap-3">
                        <folder.icon className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                        <span className="text-sm font-medium">{folder.label}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">0</Badge>
                    </button>
                  ))}
                </div>

                <div className="border-t border-border/60 pt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Connection</p>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/10 border border-destructive/20">
                    <div className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
                    <span className="text-sm font-medium text-destructive">Disconnected</span>
                  </div>
                  <Button onClick={startGmailAuth} className="w-full mt-3" disabled={loading}>
                    <Mail className="w-4 h-4 mr-2" />
                    {loading ? 'Connecting...' : 'Connect Gmail'}
                  </Button>
                </div>
              </Card>
            </div>

            {/* Main Content - Empty State */}
            <div className="lg:col-span-9">
              <Card className="relative overflow-hidden border-border/60 bg-card/80 backdrop-blur-sm shadow-sm">
                {/* Decorative gradient */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute -top-24 -right-24 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
                  <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
                </div>

                <div className="relative flex items-center justify-center px-6 py-16 md:py-20">
                  <div className="text-center space-y-6 max-w-lg animate-fade-in">
                    <div className="w-20 h-20 mx-auto rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center animate-scale-in shadow-sm">
                      <Mail className="w-10 h-10 text-primary" />
                    </div>
                    <div className="space-y-3">
                      <h2 className="text-3xl font-bold tracking-tight text-foreground">Connect Your Gmail</h2>
                      <p className="text-muted-foreground leading-relaxed">
                        Sync your inbox to manage emails, send AI-powered replies, and handle every customer conversation in one place.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                      <Button onClick={startGmailAuth} size="lg" className="shadow-sm" disabled={loading}>
                        <Mail className="w-4 h-4 mr-2" />
                        {loading ? 'Connecting...' : 'Connect Gmail Account'}
                      </Button>
                      <Button variant="outline" size="lg" onClick={() => setIsComposing(true)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Compose
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border/50 bg-background/95 backdrop-blur sticky top-0 z-20">
        <div className="px-4 md:px-6 py-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="rounded-full shrink-0"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold text-foreground">Mail</h1>
                <p className="text-sm text-muted-foreground truncate">
                  Gmail-style workspace for inbox, replies, drafts, and Bridgette
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[220px] flex-1 lg:w-[320px] lg:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search mail"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" size="sm" onClick={() => accessToken && fetchGmailMessages(accessToken)}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={askBridgette}>
                <Sparkles className="w-4 h-4 mr-2" />
                Bridgette
              </Button>
              <Dialog open={isComposing} onOpenChange={setIsComposing}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Edit className="w-4 h-4 mr-2" />
                    Compose
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>New Email</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                      <span>Draft with Bridgette or write manually.</span>
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={askBridgette}>
                        <Sparkles className="w-3.5 h-3.5 mr-1" />
                        Ask Bridgette
                      </Button>
                    </div>
                    <Input
                      placeholder="To"
                      value={composeForm.to}
                      onChange={(e) => setComposeForm({ ...composeForm, to: e.target.value })}
                    />
                    <Input
                      placeholder="Subject"
                      value={composeForm.subject}
                      onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                    />
                    <div className="border rounded-lg p-4 bg-muted/40">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">AI Email Writer</span>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Describe what the email should say..."
                          value={aiDescription}
                          onChange={(e) => setAiDescription(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && generateAIContent()}
                          disabled={isGeneratingContent}
                        />
                        <Button
                          onClick={generateAIContent}
                          disabled={isGeneratingContent || !aiDescription.trim()}
                          size="sm"
                        >
                          {isGeneratingContent ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Generate
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    <Textarea
                      placeholder="Message"
                      rows={12}
                      value={composeForm.body}
                      onChange={(e) => setComposeForm({ ...composeForm, body: e.target.value })}
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setIsComposing(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCompose}>Send</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Button variant="outline" size="sm" onClick={disconnectGmail}>
                Disconnect
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-4">
        <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="space-y-4">
            <Card className="p-3">
              <Button className="w-full justify-start h-11" onClick={() => setIsComposing(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Compose
              </Button>
              <div className="mt-4 space-y-1">
                {folders.map((folder) => {
                  const count = folderCounts[folder.id as keyof typeof folderCounts] || 0;
                  const isActive = selectedFolder === folder.id;
                  return (
                    <button
                      key={folder.id}
                      onClick={() => setSelectedFolder(folder.id)}
                      className={`w-full flex items-center justify-between rounded-md px-3 py-2.5 text-sm transition-colors ${
                        isActive ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                      }`}
                    >
                      <span className="flex items-center gap-3 min-w-0">
                        <folder.icon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{folder.label}</span>
                      </span>
                      <span className={`min-w-7 rounded-full px-2 py-0.5 text-xs font-semibold ${
                        count > 0 ? "bg-primary/12 text-primary" : "bg-muted text-muted-foreground"
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Card>

            <Card className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Mailbox</span>
                <span className="text-xs text-success">Connected</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-md bg-muted/50 px-3 py-2">
                  <div className="text-muted-foreground text-xs">Inbox</div>
                  <div className="text-lg font-semibold text-foreground">{mailboxCounts.inbox}</div>
                </div>
                <div className="rounded-md bg-muted/50 px-3 py-2">
                  <div className="text-muted-foreground text-xs">Unread</div>
                  <div className="text-lg font-semibold text-foreground">{mailboxCounts.unread}</div>
                </div>
                <div className="rounded-md bg-muted/50 px-3 py-2">
                  <div className="text-muted-foreground text-xs">Starred</div>
                  <div className="text-lg font-semibold text-foreground">{mailboxCounts.flagged}</div>
                </div>
                <div className="rounded-md bg-muted/50 px-3 py-2">
                  <div className="text-muted-foreground text-xs">Sent</div>
                  <div className="text-lg font-semibold text-foreground">{mailboxCounts.sent}</div>
                </div>
              </div>
            </Card>
          </aside>

          <div className="grid min-h-[calc(100vh-10rem)] gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
            <Card className={`${selectedEmail ? 'hidden lg:flex' : 'flex'} flex-col overflow-hidden`}>
              <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-foreground capitalize">{selectedFolder}</p>
                  <p className="text-xs text-muted-foreground">
                    {filteredEmails.length} visible message{filteredEmails.length === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
              <ScrollArea className="h-[calc(100vh-15rem)]">
                {loading ? (
                  <div className="p-6 text-sm text-muted-foreground">Loading mail...</div>
                ) : filteredEmails.length === 0 ? (
                  <div className="p-6 text-sm text-muted-foreground">
                    {searchQuery.trim() ? 'No messages match your search.' : 'No messages in this folder.'}
                  </div>
                ) : (
                  <div className="divide-y divide-border/50">
                    {filteredEmails.map((email) => {
                      const isSelected = selectedEmail?.id === email.id;
                      return (
                        <button
                          key={email.id}
                          onClick={() => {
                            setSelectedEmail(email);
                            markAsRead(email.id);
                          }}
                          className={`w-full px-4 py-3 text-left transition-colors ${
                            isSelected ? 'bg-muted' : 'hover:bg-muted/50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                {!email.is_read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                                <span className={`truncate text-sm ${!email.is_read ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
                                  {email.from_name || email.from_email}
                                </span>
                                {email.is_starred && <Star className="w-3.5 h-3.5 fill-warning text-warning shrink-0" />}
                              </div>
                              <div className={`mt-1 truncate text-sm ${!email.is_read ? 'font-semibold text-foreground' : 'text-foreground'}`}>
                                {email.subject}
                              </div>
                              <div className="mt-1 truncate text-xs text-muted-foreground">{email.body_text}</div>
                            </div>
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {email.created_at && formatDistanceToNow(new Date(email.created_at), { addSuffix: true })}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </Card>

            <Card className="flex flex-col overflow-hidden">
              {selectedEmail ? (
                <>
                  <div className="flex items-center justify-between gap-3 border-b border-border/50 px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="lg:hidden"
                        onClick={() => setSelectedEmail(null)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <div className="min-w-0">
                        <h2 className="truncate text-xl font-semibold text-foreground">{selectedEmail.subject}</h2>
                        <p className="truncate text-sm text-muted-foreground">
                          {selectedEmail.from_name || selectedEmail.from_email}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => generateAIReply(selectedEmail)}
                        disabled={isGeneratingReply}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        {isGeneratingReply ? 'Generating...' : 'Reply with AI'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleStar(selectedEmail.id, selectedEmail.is_starred)}
                      >
                        <Star className={`w-4 h-4 ${selectedEmail.is_starred ? 'fill-warning text-warning' : 'text-muted-foreground'}`} />
                      </Button>
                    </div>
                  </div>
                  <ScrollArea className="h-[calc(100vh-15rem)]">
                    <div className="mx-auto max-w-4xl px-5 py-5">
                      <div className="mb-6 rounded-lg border border-border bg-muted/20 p-4">
                        <div className="flex flex-col gap-1 text-sm">
                          <div><span className="text-muted-foreground">From:</span> <span className="text-foreground">{selectedEmail.from_email}</span></div>
                          <div><span className="text-muted-foreground">To:</span> <span className="text-foreground">{selectedEmail.to_email?.join(', ')}</span></div>
                          <div><span className="text-muted-foreground">Received:</span> <span className="text-foreground">{new Date(selectedEmail.created_at).toLocaleString()}</span></div>
                        </div>
                      </div>
                      {selectedEmail.body_html ? (
                        <div
                          className="prose prose-sm max-w-none text-foreground dark:prose-invert prose-p:my-3 prose-a:text-primary prose-li:my-1"
                          dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }}
                        />
                      ) : (
                        <div className="whitespace-pre-wrap text-[15px] leading-7 text-foreground">
                          {selectedEmail.body_text}
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </>
              ) : (
                <div className="flex h-[calc(100vh-15rem)] items-center justify-center p-8 text-center">
                  <div className="max-w-md space-y-3">
                    <h2 className="text-2xl font-semibold text-foreground">Your mail, one workspace</h2>
                    <p className="text-sm text-muted-foreground">
                      Open a message from the list to read it full-width, draft a new email, or ask Bridgette to write and triage for you.
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <Button onClick={() => setIsComposing(true)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Compose
                      </Button>
                      <Button variant="outline" onClick={askBridgette}>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Ask Bridgette
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EmailWorkspace() {
  return <EmailWorkspaceContent />;
}
